import React from 'react';
import { Box, Paper, Typography, Grid, useTheme, useMediaQuery, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const stepItems = [
  {
    stepNumber: '01',
    icon: <AssignmentIcon sx={{ fontSize: 30, color: '#ffffff' }} />,
    title: '간편 접수',
    desc: '기기 정보 입력 후 보상판매 접수',
    subDesc: '사진 없이도 간편하게 신청 가능',
    color: '#3b82f6',
    bgGradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
  },
  {
    stepNumber: '02',
    icon: <VerifiedUserIcon sx={{ fontSize: 30, color: '#ffffff' }} />,
    title: '전문가의 검수',
    desc: '기기 회수후 전문가의 검수 진행',
    subDesc: '영업일 기준 2~3일 소요',
    color: '#8b5cf6',
    bgGradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  },
  {
    stepNumber: '03',
    icon: <AccountBalanceWalletIcon sx={{ fontSize: 30, color: '#ffffff' }} />,
    title: '계좌입금',
    desc: '내 기기 판매가격 확인후 입금받기!',
    subDesc: '판매 선택시 다음 영업일에 입금',
    color: '#06b6d4',
    bgGradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
  },
];

export const GuideSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      sx={{
        maxWidth: 900,
        mx: 'auto',
        zIndex: 2,
        mb: { xs: 3, md: 5 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          mb: 3,
          background: 'rgba(255,255,255,0.47)',         // ✅ 배경 통일
          backdropFilter: 'blur(18px)',                 // ✅ 블러 통일
          borderRadius: 6,
          boxShadow: '0 4px 36px 0 rgba(43,64,176,0.09)', // ✅ 그림자 통일
          overflow: 'visible',
          border: '1px solid rgba(255,255,255,0.2)',      // ✅ 테두리 통일
        }}
      >
        <Typography
          variant={isMobile ? 'h6' : 'h4'}
          align="center"
          fontWeight={800}
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            letterSpacing: -0.5,
            mb: 0.5,
          }}
        >
          중고 전자기기 보상판매
        </Typography>

        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Chip
            label="3 STEP 간편신청"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              color: 'white',
              fontWeight: 700,
              fontSize: isMobile ? 11 : 13,
              height: 26,
              mb: 1.5,
            }}
          />
        </Box>

        <Typography
          align="center"
          fontWeight={600}
          fontSize={isMobile ? 13 : 15}
          sx={{
            color: '#334155',
            opacity: 0.9,
            mb: 4,
            letterSpacing: 0.2,
          }}
        >
          빠르고 간편하게,{' '}
          <span style={{ color: '#3b82f6', fontWeight: 800 }}>3분안에 신청 완료!</span>
        </Typography>

        <Grid container spacing={isMobile ? 2 : 3} sx={{ position: 'relative' }}>
          {stepItems.map((step, idx) => (
            <Grid item xs={12} md={4} key={idx} sx={{ position: 'relative' }}>
              {!isMobile && idx < stepItems.length - 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: -16,
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                  }}
                >
                  <ArrowForwardIcon
                    sx={{
                      fontSize: 20,
                      color: '#cbd5e1',
                      opacity: 0.7,
                    }}
                  />
                </Box>
              )}

              <Paper
                component={motion.div}
                whileHover={{
                  scale: 1.04,
                  y: -10,
                  boxShadow: `0 16px 32px 0 ${step.color}1f`,
                }}
                transition={{ type: 'spring', stiffness: 180, damping: 20 }}
                elevation={0}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  borderRadius: 4,
                  background: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: `0 6px 24px 0 ${step.color}10`,
                  border: `1.5px solid ${step.color}30`,
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 42,
                    height: 24,
                    background: step.bgGradient,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 2px 12px 0 ${step.color}40`,
                  }}
                >
                  <Typography
                    fontWeight={900}
                    fontSize={12}
                    color="white"
                    sx={{ letterSpacing: 0.5 }}
                  >
                    {step.stepNumber}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    mx: 'auto',
                    mt: 1.5,
                    mb: 2,
                    background: step.bgGradient,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 6px 20px 0 ${step.color}30`,
                  }}
                >
                  {step.icon}
                </Box>

                <Typography
                  fontWeight={700}
                  fontSize={isMobile ? 15 : 16}
                  sx={{
                    color: '#1e293b',
                    mb: 1,
                    letterSpacing: -0.2,
                  }}
                >
                  {step.title}
                </Typography>

                <Typography
                  fontSize={isMobile ? 12 : 13}
                  sx={{
                    color: '#475569',
                    fontWeight: 500,
                    mb: step.subDesc ? 0.8 : 0,
                    lineHeight: 1.5,
                  }}
                >
                  {step.desc}
                </Typography>

                {step.subDesc && (
                  <Typography
                    fontSize={11}
                    sx={{
                      color: step.color,
                      fontWeight: 600,
                      fontStyle: 'italic',
                    }}
                  >
                    *{step.subDesc}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
};
